import React, { forwardRef, useRef, useState, useMemo, useEffect, cloneElement, Children, isValidElement } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { useTheme } from 'react-jss';
import { Fade } from '@wds/motion';
import { safeRest, createMemoStyles, defaultFonts, uniqueId, isBrowser, isElement } from '@wds/utils';
import { List } from '@wds/list';

function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

function _arrayLikeToArray(arr, len) {
  if (len == null || len > arr.length) len = arr.length;

  for (var i = 0, arr2 = new Array(len); i < len; i++) {
    arr2[i] = arr[i];
  }

  return arr2;
}

function _arrayWithoutHoles(arr) {
  if (Array.isArray(arr)) return _arrayLikeToArray(arr);
}

function _iterableToArray(iter) {
  if (typeof Symbol !== "undefined" && iter[Symbol.iterator] != null || iter["@@iterator"] != null) return Array.from(iter);
}

function _unsupportedIterableToArray(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(o);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen);
}

function _nonIterableSpread() {
  throw new TypeError("Invalid attempt to spread non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}

function _toConsumableArray(arr) {
  return _arrayWithoutHoles(arr) || _iterableToArray(arr) || _unsupportedIterableToArray(arr) || _nonIterableSpread();
}

function _arrayWithHoles(arr) {
  if (Array.isArray(arr)) return arr;
}

function _iterableToArrayLimit(arr, i) {
  var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"];

  if (_i == null) return;
  var _arr = [];
  var _n = true;
  var _d = false;

  var _s, _e;

  try {
    for (_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true) {
      _arr.push(_s.value);

      if (i && _arr.length === i) break;
    }
  } catch (err) {
    _d = true;
    _e = err;
  } finally {
    try {
      if (!_n && _i["return"] != null) _i["return"]();
    } finally {
      if (_d) throw _e;
    }
  }

  return _arr;
}

function _nonIterableRest() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}

function _slicedToArray(arr, i) {
  return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _unsupportedIterableToArray(arr, i) || _nonIterableRest();
}

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

function _objectWithoutProperties(source, excluded) {
  if (source == null) return {};

  var target = _objectWithoutPropertiesLoose(source, excluded);

  var key, i;

  if (Object.getOwnPropertySymbols) {
    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);

    for (i = 0; i < sourceSymbolKeys.length; i++) {
      key = sourceSymbolKeys[i];
      if (excluded.indexOf(key) >= 0) continue;
      if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
      target[key] = source[key];
    }
  }

  return target;
}

var SEMANTIC_COLORS = /* #__PURE__ */Object.freeze(['primary', 'success', 'info', 'warning', 'error', 'upgrade', 'secondary', 'muted']);
var SIZES = /* #__PURE__ */Object.freeze(['lg', 'md', 'sm']);
/* #__PURE__ */

Object.freeze([].concat(_toConsumableArray(SIZES), ['xs']));
var DATA_VIZ_THEMES = /* #__PURE__ */Object.freeze(['data-one', 'data-two', 'data-three', 'data-four', 'data-five', 'data-six', 'data-seven', 'data-eight', 'data-nine', 'data-ten']);
var KEYS = /* #__PURE__ */Object.freeze({
  DOWN: 'ArrowDown',
  END: 'End',
  ESC: 'Escape',
  HOME: 'Home',
  LEFT: 'ArrowLeft',
  PAGE_DOWN: 'PageDown',
  PAGE_UP: 'PageUp',
  RIGHT: 'ArrowRight',
  UP: 'ArrowUp',
  ENTER: 'Enter',
  SPACE: ' ',
  BACKSPACE: 'Backspace',
  TAB: 'Tab'
}); // Deprecated. Moving to e.key

var _excluded$1 = ["addOn", "addOnPlacement", "color", "onClick", "children", "component", "disabled", "isNavigable", "marked", "upgrade", "groupindex", "handleMenuClose", "index", "role", "id"];
var MenuItem = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var addOn = _ref.addOn,
      addOnPlacement = _ref.addOnPlacement,
      color = _ref.color,
      onClick = _ref.onClick,
      children = _ref.children,
      component = _ref.component,
      disabled = _ref.disabled,
      isNavigable = _ref.isNavigable,
      marked = _ref.marked,
      upgrade = _ref.upgrade,
      groupindex = _ref.groupindex,
      handleMenuClose = _ref.handleMenuClose,
      index = _ref.index,
      role = _ref.role,
      id = _ref.id,
      rest = _objectWithoutProperties(_ref, _excluded$1);

  var onHandleClick = function onHandleClick(e) {
    if (onClick) onClick(e);
    if (handleMenuClose) handleMenuClose(e);
    e.stopPropagation();
  };

  return /*#__PURE__*/React.createElement(List.Item, Object.assign({
    addOnLeft: addOnPlacement === 'left' ? addOn : undefined,
    addOnRight: addOnPlacement === 'right' ? addOn : undefined,
    color: color,
    component: component,
    "data-group": groupindex,
    disabled: disabled,
    index: index,
    isNavigable: isNavigable,
    marked: marked,
    menu: true,
    onClick: onHandleClick,
    role: role,
    upgrade: upgrade,
    tabIndex: disabled ? -1 : 0,
    ref: ref,
    id: id
  }, safeRest(rest)), children);
});
MenuItem.propTypes = {
  /**
   * Add-on for menu items just as you would use them for ListItems
   */
  addOn: PropTypes.node,

  /**
   * Specify the add-on placement within Item
   */
  addOnPlacement: PropTypes.oneOf(['left', 'right']),

  /**
   * Menu Item content
   */
  children: PropTypes.node.isRequired,

  /**
   * Color variants
   */
  color: PropTypes.oneOfType([PropTypes.oneOf(SEMANTIC_COLORS), PropTypes.oneOf(DATA_VIZ_THEMES)]),

  /**
   * In combination with `isNavigable` you can pass a custom component that will be
   * rendered in place of a regular a element. By default it is `a` if isNaviagable is true
   */
  component: PropTypes.func,

  /**
   * If true, item will be disabled
   */
  disabled: PropTypes.bool,

  /**
   * Menu group index
   */
  groupindex: PropTypes.number,

  /**
   * Data index of current ListItem
   */
  index: PropTypes.number,

  /**
   * If true, add a mark to the left-hand side of a item.
   * Take notice that the label does not shift to accommodate for the mark;
   * it stays in its original location.
   */
  marked: PropTypes.bool,

  /**
   * onClick handler for ListItem
   * function(event: object) => void
   */
  onClick: PropTypes.func,

  /**
   * Add special "upgrade" highlight styles to item.
   */
  upgrade: PropTypes.bool,

  /**
   * If true, item can be a navigational component
   */
  isNavigable: PropTypes.bool,

  /**
   * The href location of your nav target
   */
  navigationTarget: PropTypes.string,

  /**
   * If true, ListItem will be focused on mount
   */
  isFocused: PropTypes.bool,

  /**
   * on Menu close handler
   * function(event: object) => void
   */
  handleMenuClose: PropTypes.func,

  /**
   * Aria role for Menu Item
   */
  role: PropTypes.string,
  id: PropTypes.string
};
MenuItem.defaultProps = {
  addOn: undefined,
  addOnPlacement: 'left',
  color: undefined,
  component: undefined,
  disabled: false,
  groupindex: undefined,
  index: undefined,
  marked: false,
  onClick: undefined,
  upgrade: false,
  isNavigable: false,
  navigationTarget: undefined,
  isFocused: false,
  handleMenuClose: undefined,
  role: 'menuitem',
  id: undefined
};
MenuItem.displayName = 'MenuItem';
var useStyles$1 = createMemoStyles(function (theme) {
  var border = theme.border;
  return {
    wrenchMenuGroup: function wrenchMenuGroup() {
      return {
        borderBottom: border.main,
        '&:last-child': {
          borderBottom: 'none'
        }
      };
    }
  };
});

var MenuGroup = function MenuGroup(_ref) {
  var children = _ref.children;
  var theme = useTheme();
  var classes = useStyles$1({
    theme: theme
  });
  return /*#__PURE__*/React.createElement("section", {
    className: classes.wrenchMenuGroup
  }, children);
};

MenuGroup.displayName = "MenuGroup";
MenuGroup.propTypes = {
  children: PropTypes.node.isRequired
};

function ownKeys$1(object, enumerableOnly) {
  var keys = Object.keys(object);

  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);

    if (enumerableOnly) {
      symbols = symbols.filter(function (sym) {
        return Object.getOwnPropertyDescriptor(object, sym).enumerable;
      });
    }

    keys.push.apply(keys, symbols);
  }

  return keys;
}

function _objectSpread$1(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i] != null ? arguments[i] : {};

    if (i % 2) {
      ownKeys$1(Object(source), true).forEach(function (key) {
        _defineProperty(target, key, source[key]);
      });
    } else if (Object.getOwnPropertyDescriptors) {
      Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
      ownKeys$1(Object(source)).forEach(function (key) {
        Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
      });
    }
  }

  return target;
}

var useStyles = createMemoStyles(function (theme) {
  var palette = theme.palette,
      shadow = theme.shadow,
      radius = theme.radius;
  var menu = {
    boxShadow: shadow.space,
    maxWidth: 300,
    minWidth: 175,
    maxHeight: 325
  };
  return {
    wrenchMenuContainer: function wrenchMenuContainer(_ref) {
      var stretched = _ref.stretched;
      return _objectSpread$1(_objectSpread$1({}, defaultFonts(theme)), {}, {
        position: 'relative',
        display: stretched ? 'block' : 'inline-block',
        outline: 'none'
      });
    },
    wrenchMenu: function wrenchMenu(_ref2) {
      var menuStyle = _ref2.menuStyle,
          placement = _ref2.placement,
          stretched = _ref2.stretched,
          elevation = _ref2.elevation;
      return _objectSpread$1(_objectSpread$1({
        zIndex: elevation || theme.elevation.sky,
        display: 'block',
        position: 'absolute',
        outline: 'none',
        textAlign: 'left',
        color: palette.text.dark,
        minWidth: menu.minWidth,
        maxWidth: stretched ? 'none' : menu.maxWidth,
        boxShadow: menu.boxShadow,
        background: palette.background.light,
        borderRadius: radius.sm,
        whiteSpace: 'noWrap'
      }, placement && {
        'bottom-left': {
          left: 0,
          top: '100%'
        },
        'bottom-right': {
          left: 'auto',
          right: 0,
          top: '100%'
        },
        'top-left': {
          left: 0,
          top: 'auto',
          bottom: '100%'
        },
        'top-right': {
          left: 'auto',
          right: 0,
          top: 'auto',
          bottom: '100%'
        }
      }[placement]), menuStyle);
    },
    wrenchMenuBody: function wrenchMenuBody(_ref3) {
      var noMaxHeight = _ref3.noMaxHeight;
      return {
        maxHeight: noMaxHeight ? 'none' : menu.maxHeight,
        overflowY: 'auto'
      };
    }
  };
});
var _excluded = ["autoClose", "children", "dense", "placement", "menuTrigger", "noMaxHeight", "onChange", "show", "stretched", "renderTo", "role", "noRules", "elevation"];

function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);

  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);

    if (enumerableOnly) {
      symbols = symbols.filter(function (sym) {
        return Object.getOwnPropertyDescriptor(object, sym).enumerable;
      });
    }

    keys.push.apply(keys, symbols);
  }

  return keys;
}

function _objectSpread(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i] != null ? arguments[i] : {};

    if (i % 2) {
      ownKeys(Object(source), true).forEach(function (key) {
        _defineProperty(target, key, source[key]);
      });
    } else if (Object.getOwnPropertyDescriptors) {
      Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
      ownKeys(Object(source)).forEach(function (key) {
        Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
      });
    }
  }

  return target;
}

var MENU_PLACEMENTS = ['bottom-right', 'bottom-left', 'top-right', 'top-left'];
var FOCUS_STATE = Object.freeze({
  TOP: 'top',
  BOTTOM: 'bottom'
});

var Menu = function Menu(_ref) {
  var autoClose = _ref.autoClose,
      children = _ref.children,
      dense = _ref.dense,
      placement = _ref.placement,
      menuTrigger = _ref.menuTrigger,
      noMaxHeight = _ref.noMaxHeight,
      onChange = _ref.onChange,
      showProp = _ref.show,
      stretched = _ref.stretched,
      renderTo = _ref.renderTo,
      role = _ref.role,
      noRules = _ref.noRules,
      elevation = _ref.elevation,
      rest = _objectWithoutProperties(_ref, _excluded); // Refs


  var menuRef = useRef(null);
  var innerMenuRef = useRef(null);
  var itemsRef = useRef(null); // Menu State Items

  var _useState = useState(showProp),
      _useState2 = _slicedToArray(_useState, 2),
      showState = _useState2[0],
      setShowState = _useState2[1];

  var _useState3 = useState({}),
      _useState4 = _slicedToArray(_useState3, 2),
      menuStyle = _useState4[0],
      setMenuStyle = _useState4[1]; //


  var _useState5 = useState(null),
      _useState6 = _slicedToArray(_useState5, 2),
      focusState = _useState6[0],
      setFocusState = _useState6[1];

  var _useState7 = useState(-1),
      _useState8 = _slicedToArray(_useState7, 2),
      focusIndex = _useState8[0],
      setFocusIndex = _useState8[1]; // aria-owns for a11y


  var id = useMemo(function () {
    return uniqueId('wds-menu-');
  }, []); // Styles

  var theme = useTheme();
  var classes = useStyles({
    menuStyle: menuStyle,
    noMaxHeight: noMaxHeight,
    placement: placement,
    stretched: stretched,
    elevation: elevation,
    theme: theme
  });
  /**
   * a way to help replicate componentWillReceiveProps
   * and componentDidUpdate
   */

  var prevPropsRef = useRef(null);
  useEffect(function () {
    prevPropsRef.current = {
      showProp: showProp,
      showState: showState
    };
    /**
     * Each time the MenuItem List is generated through the menu button trigger
     * this code will create a list of accessible ListItems to traverse through.
     */

    if (showState && innerMenuRef.current) {
      itemsRef.current = _toConsumableArray(innerMenuRef.current.getElementsByTagName('li')).map(function (item) {
        return item.firstChild;
      });
    }
    /**
     * When showProp is still set to True, but showState is false,
     * we need sync showState up with showProp
     */


    if (showProp && showState !== showProp) {
      setShowState(showProp);
    }
  }, [showProp, showState, children]);
  var prevProps = prevPropsRef.current; // This will auto focus the first or last item

  useEffect(function () {
    if (focusState) {
      var focusInd = focusState === FOCUS_STATE.TOP ? 0 : itemsRef.current.length - 1;

      if (itemsRef.current[focusInd]) {
        itemsRef.current[focusInd].focus();
      }

      setFocusIndex(focusInd);
    }
  }, [focusState]);
  /**
   * @todo needs Modal
   * Update the placement of the menu when renderTo is passed in.
   */

  var updatePosition = function updatePosition() {
    if (menuRef.current && showState && innerMenuRef.current.children.length) {
      var rect = menuRef.current.getBoundingClientRect();
      var innerRect = innerMenuRef.current.getBoundingClientRect();
      var coordinates = {
        'top-left': {
          top: rect.top - innerRect.height,
          left: rect.left
        },
        'top-right': {
          top: rect.top - innerRect.height,
          left: rect.right - innerRect.width
        },
        'bottom-left': {
          top: rect.bottom,
          left: rect.left
        }
      }[placement] || {
        top: rect.bottom,
        left: rect.right - innerRect.width
      };
      var top = coordinates.top,
          left = coordinates.left;
      var z = (elevation || theme.elevation.space) + 1;
      setMenuStyle({
        top: top,
        left: left,
        zIndex: z,
        position: 'fixed',
        right: 'auto',
        // fixes menu too wide
        bottom: 'auto' // fixes box-shadow not showing

      });
    }
  };

  var handleScroll = function handleScroll() {
    if (showState && renderTo) {
      updatePosition();
    }
  }; // componentDidMount


  useEffect(function () {
    if (showState && renderTo) updatePosition(); // ssr doesn't have window object

    if (isBrowser) {
      window.addEventListener('scroll', handleScroll);
      window.addEventListener('resize', handleScroll);
      window.addEventListener('orientationchange', handleScroll);
    } // componentWillUnmount


    return function () {
      if (isBrowser) {
        window.removeEventListener('scroll', handleScroll);
        window.removeEventListener('resize', handleScroll);
        window.removeEventListener('orientationchange', handleScroll);
      }
    }; // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [renderTo, showState]); // componentDidUpdate

  useEffect(function () {
    if (prevProps) {
      if (!prevProps.showState && showProp && renderTo) {
        updatePosition();
      }
    }
  }); // componentWillReceiveProps

  useEffect(function () {
    setShowState(showProp);
  }, [showProp]);

  var menuChange = function menuChange(showMenu) {
    if (!showMenu) {
      setFocusState(null);
      setFocusIndex(-1);
    }

    setShowState(showMenu);

    if (onChange) {
      onChange(showMenu);
    }
  };

  var handleOnClick = function handleOnClick(e) {
    menuChange(!showState);
    e.stopPropagation();
  };

  var handleKeyScroll = function handleKeyScroll(index) {
    if (itemsRef.current) {
      if (index > -1 && itemsRef.current[index]) {
        var menuItemHeight = itemsRef.current[index].offsetTop + itemsRef.current[index].offsetHeight;
        var menuContainerHeight = innerMenuRef.offsetHeight;
        innerMenuRef.current.scrollTop = menuItemHeight <= menuContainerHeight ? 0 : menuItemHeight - menuContainerHeight;
      }
    }
  };

  var handleOnKeyDown = function handleOnKeyDown(e) {
    var key = e.key;

    if (showState) {
      var itemsLen = itemsRef.current.length - 1;
      var focusInd = focusIndex === -1 ? 0 : focusIndex;
      var newFocusIndex = 0;

      switch (key) {
        case KEYS.TAB:
        case KEYS.ESC:
          menuChange(false);

          if (menuRef.current.firstChild) {
            menuRef.current.firstChild.focus();
          }

          e.preventDefault();
          break;

        /**
         * TODO: Needs to handle space in a better way
         */

        case KEYS.ENTER:
          itemsRef.current[focusInd].click();
          break;

        case KEYS.UP:
          newFocusIndex = focusInd > 0 ? focusInd - 1 : itemsLen;
          break;

        case KEYS.DOWN:
          newFocusIndex = focusInd === itemsLen ? 0 : focusInd + 1;
          break;
      }

      if (key === KEYS.UP || key === KEYS.DOWN) {
        setFocusIndex(newFocusIndex);
        handleKeyScroll(newFocusIndex);

        if (newFocusIndex > -1 && itemsRef.current[newFocusIndex]) {
          itemsRef.current[newFocusIndex].focus();
          e.preventDefault();
        }
      }
    }
  };

  var handleOnTriggerKeyDown = function handleOnTriggerKeyDown(e) {
    var key = e.key;

    if (menuTrigger.props.onKeyDown) {
      menuTrigger.props.onKeyDown(e);
    }

    var isNotInput = menuTrigger.type && menuTrigger.type.displayName !== 'Input';

    if (isNotInput) {
      switch (key) {
        case KEYS.ENTER:
        case KEYS.DOWN:
          handleOnClick(e);
          setFocusState(FOCUS_STATE.TOP);
          e.preventDefault();
          break;

        case KEYS.UP:
          handleOnClick(e);
          setFocusState(FOCUS_STATE.BOTTOM);
          e.preventDefault();
          break;
      }
    }
  };

  var handleAutoCloseBlur = function handleAutoCloseBlur() {
    setTimeout(function () {
      var renderToComp = isElement(renderTo) || renderTo && renderTo.current;
      var isFocusInRenderContainer = renderToComp && renderToComp.contains(document.activeElement);
      var isFocusInMenuRef = menuRef.current && menuRef.current.contains(document.activeElement);

      if (!isFocusInRenderContainer && !isFocusInMenuRef) {
        menuChange(false);
      }
    }, 0);
  };

  var handleMenuClose = function handleMenuClose() {
    if (autoClose) {
      menuChange(false);
    }
  };

  var renderListItems = function renderListItems(ch) {
    var groupindex = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 0;
    var handleMenu = arguments.length > 2 ? arguments[2] : undefined;
    return Children.map(ch, function (child) {
      if (! /*#__PURE__*/isValidElement(child)) return null;
      return /*#__PURE__*/cloneElement(child, {
        groupindex: groupindex,
        // isFocused,
        onKeyDown: handleOnKeyDown,
        handleMenuClose: handleMenu
      });
    });
  };

  var renderList = function renderList() {
    var items = Children.map(children, function (child, index) {
      if (! /*#__PURE__*/isValidElement(child)) return;
      if (child.type.displayName !== 'MenuGroup') return; // eslint-disable-next-line consistent-return

      return /*#__PURE__*/React.createElement(MenuGroup, null, /*#__PURE__*/React.createElement(List, Object.assign({
        interactive: true,
        noRules: true,
        dense: dense
      }, rest), renderListItems(child.props.children, index, handleMenuClose)));
    });
    if (items.length) return items;
    return /*#__PURE__*/React.createElement(List, Object.assign({
      interactive: true,
      dense: dense,
      noRules: noRules
    }, rest), children && renderListItems(children, 0, handleMenuClose));
  };

  var renderMenu = function renderMenu() {
    var MenuComponent = /*#__PURE__*/React.createElement(Fade, {
      in: showState,
      speed: "fast"
    }, /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
      className: classes.wrenchMenu,
      role: role,
      id: id
    }, /*#__PURE__*/React.createElement("div", {
      className: classes.wrenchMenuBody,
      ref: innerMenuRef
    }, renderList()))));

    if (isElement(renderTo) || renderTo && renderTo.current) {
      return /*#__PURE__*/createPortal(MenuComponent, isElement(renderTo) ? renderTo : renderTo.current);
    }

    return MenuComponent;
  };

  var renderTrigger = function renderTrigger() {
    var componentName = menuTrigger.type.displayName;
    var isButton = componentName === 'Button';
    var isInput = componentName === 'Input';
    return /*#__PURE__*/cloneElement(menuTrigger, _objectSpread(_objectSpread(_objectSpread({
      'aria-haspopup': role,
      'aria-expanded': !isInput ? showState : undefined
    }, showState && {
      'aria-owns': id
    }), isButton && {
      ariaPressed: showState || showProp
    }), {}, {
      onFocus: isInput ? handleOnClick : undefined,
      onClick: !isInput ? handleOnClick : undefined,
      onKeyDown: handleOnTriggerKeyDown,
      tabIndex: 0
    }));
  };

  return /*#__PURE__*/React.createElement("span", Object.assign({
    className: classes.wrenchMenuContainer,
    onBlur: autoClose ? handleAutoCloseBlur : undefined,
    ref: menuRef,
    tabIndex: -1
  }, safeRest(rest)), renderTrigger(), renderMenu());
};

Menu.displayName = "Menu";
Menu.propTypes = {
  /**
   * If true, Menu will close automatically on click away
   */
  autoClose: PropTypes.bool,

  /**
   * Composed of Menu.Item
   */
  children: PropTypes.node.isRequired,

  /**
   * Reduce child Menu.Item height and font-weight
   */
  dense: PropTypes.bool,

  /**
   * You can anchor your menu to any corner on the north or south of the element that summoned it.
   */
  placement: PropTypes.oneOf(MENU_PLACEMENTS),

  /**
   * Required prop that will open or close the menu
   */
  menuTrigger: PropTypes.node.isRequired,

  /**
   * If true, Menu will not limit a max height of 325px
   */
  noMaxHeight: PropTypes.bool,

  /**
   * onChange handler
   * function(event: object) => void
   */
  onChange: PropTypes.func,

  /**
   * If true, menu items are displayed
   */
  show: PropTypes.bool,

  /**
   *
   */
  stretched: PropTypes.bool,

  /**
   *  You may want to render the menu attached to a parent element so that it can be rendered outside of the trigger container.
   */
  renderTo: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({
    current: PropTypes.elementType
  })]),

  /**
   * Define menu ARIA role
   */
  role: PropTypes.string,

  /**
   * Remove divider lines between List.Item
   */
  noRules: PropTypes.bool,

  /**
   * If provided, overrides the z-index value of the component
   */
  elevation: PropTypes.number
};
Menu.defaultProps = {
  autoClose: true,
  dense: false,
  placement: undefined,
  noMaxHeight: false,
  onChange: undefined,
  stretched: false,
  show: false,
  renderTo: undefined,
  role: 'menu',
  noRules: false,
  elevation: undefined
};
Menu.Item = MenuItem;
Menu.Group = MenuGroup;
export { Menu };